import { Box, Typography, Button, Container } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useNavigate, useSearchParams } from "react-router-dom"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import { USER_REPORTS_ROUTE } from "../../../../../../../constants/routes"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { useMemo } from "react"
import { TranslationKey } from "../../../../../../localization/translations"
import { PageHeader } from "../../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../../shared/components/pageHeader/PageHeaderApi"

const useStyles = makeStyles()((theme) => ({
    container: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: theme.spacing(3),
    },
    content: {
        padding: theme.spacing(4),
        maxWidth: 500,
        width: "100%",
        textAlign: "center",
    },
    title: {
        fontWeight: 600,
        marginBottom: theme.spacing(3),
        color: theme.palette.text.primary,
    },
    description: {
        color: theme.palette.text.secondary,
        lineHeight: 1.6,
        marginBottom: theme.spacing(4),
    },
}))

export type PaymentErrorType = "failed" | "canceled" | "timeout"

export interface ErrorMetadata {
    title: string
    titleId: TranslationKey
    description: string
    descriptionId: TranslationKey
}

export function UserWalletsRefillStatusFailed() {
    const { classes } = useStyles()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const type = searchParams.get("type") ?? null

    const backToDashboard = () => navigate(USER_REPORTS_ROUTE)

    const errorMeta: ErrorMetadata = useMemo(() => {
        if (type === "failed") {
            return {
                title: "Payment failed.",
                titleId: "user-wallets-refill-payment-failed-label",
                description:
                    "We haven't received confirmation from your payment provider. Please check your netbank to be sure that the payment was completed.",
                descriptionId: "user-wallets-refill-payment-failed-description",
            }
        }

        if (type === "canceled") {
            return {
                title: "Payment canceled",
                titleId: "user-wallets-refill-payment-canceled-label",
                description: "You've canceled the payment process. No charges have been made to your account.",
                descriptionId: "user-wallets-refill-payment-canceled-description",
            }
        }

        return {
            title: "Payment Timed Out",
            titleId: "user-wallets-refill-payment-timeout-label",
            description:
                "The payment processing has timed out. Please try again or contact support if the issue persists.",
            descriptionId: "user-wallets-refill-payment-timeout-description",
        }
    }, [type])

    return (
        <>
            <LocalizedStrict id="user-wallets-refill-title" attrs={{ title: true }}>
                <PageHeader page={Page.USER_WALLETS_REFILL} title="Refill wallet" />
            </LocalizedStrict>
            <Container className={classes.container}>
                <Box className={classes.content}>
                    <LocalizedStrict id={errorMeta.titleId}>
                        <Typography variant="h4" className={classes.title}>
                            {errorMeta.title}
                        </Typography>
                    </LocalizedStrict>

                    <LocalizedStrict id={errorMeta.titleId}>
                        <Typography variant="body1" className={classes.description}>
                            {errorMeta.title}
                        </Typography>
                    </LocalizedStrict>

                    <LocalizedStrict id="user-wallets-refill-payment-back-action-button-label">
                        <StandardButton defaultBorderRadius color="black" onClick={backToDashboard} fullWidth={false}>
                            Back to dashboard
                        </StandardButton>
                    </LocalizedStrict>
                </Box>
            </Container>
        </>
    )
}
